import * as React from 'react'
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Clock, Check } from 'lucide-react'
import { Drawer } from '@/components/primitives/Drawer'
import { Button } from '@/components/primitives/Button'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import { getFacilitySchedule, upsertFacilitySchedule } from './api'
import { listResidents } from '@/features/residents/api'
import { useAuth } from '@/features/auth/AuthContext'
import { useFacilityFilter } from '@/store/facilityFilterStore'
import './ScheduleDrawer.css'

interface Props {
  open: boolean
  onClose: () => void
  onOpenPatients?: () => void
}

function hhmm(hour: number, min: number): string {
  return `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`
}

function formatDisplayTime(hour: number, min: number): string {
  const ampm = hour < 12 ? 'AM' : 'PM'
  const h = hour % 12 === 0 ? 12 : hour % 12
  const m = min.toString().padStart(2, '0')
  return `${h}:${m} ${ampm}`
}

const DOW = [
  { key: 'MON', label: 'Mon' },
  { key: 'TUE', label: 'Tue' },
  { key: 'WED', label: 'Wed' },
  { key: 'THU', label: 'Thu' },
  { key: 'FRI', label: 'Fri' },
  { key: 'SAT', label: 'Sat' },
  { key: 'SUN', label: 'Sun' },
] as const

export function ScheduleDrawer({ open, onClose, onOpenPatients }: Props): React.JSX.Element {
  const qc = useQueryClient()
  const toast = useToast()
  const { user } = useAuth()
  const { facilityId: filterFacilityId } = useFacilityFilter()
  const activeFacilityId = user?.role === 'SUPER_ADMIN' ? filterFacilityId : (user?.facility_id ?? null)

  const { data: saved } = useQuery({
    queryKey: ['schedule', 'facility', activeFacilityId],
    queryFn: () => getFacilitySchedule(activeFacilityId),
    staleTime: 60_000,
  })

  const { data: residents = [] } = useQuery({
    queryKey: ['residents', activeFacilityId],
    queryFn: () => listResidents({ page_size: 200, page: 0, facility_id: activeFacilityId ?? undefined }),
    enabled: open,
    staleTime: 30_000,
  })

  const totalCount = residents.length
  const readyCount = residents.filter(r => r.pcp_fax).length

  const [enabled, setEnabled] = useState(true)
  const [cadence, setCadence] = useState<'daily' | 'weekly' | 'biweekly' | 'monthly'>('weekly')
  const [sendHour, setSendHour] = useState(8)
  const [sendMin, setSendMin] = useState(0)
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>(['MON'])
  const [dayOfMonth, setDayOfMonth] = useState(1)

  useEffect(() => {
    if (!saved) return
    setEnabled(saved.active)
    setCadence(saved.cadence)
    setSendHour((saved as { send_hour_local?: number }).send_hour_local ?? 8)
    setSendMin((saved as { send_minute_local?: number }).send_minute_local ?? 0)
    setDaysOfWeek(saved.days_of_week?.length ? saved.days_of_week : ['MON'])
    setDayOfMonth(saved.day_of_month ?? 1)
  }, [saved])

  function toggleDay(key: string): void {
    // Biweekly = "every other <day>" — only one day makes sense.
    if (cadence === 'biweekly') {
      setDaysOfWeek([key])
      return
    }
    // Weekly can be multiple days (e.g. Mon + Thu).
    setDaysOfWeek((prev) =>
      prev.includes(key)
        ? (prev.length > 1 ? prev.filter((d) => d !== key) : prev) // keep at least one
        : [...prev, key],
    )
  }

  function changeCadence(c: typeof cadence): void {
    setCadence(c)
    // Switching to biweekly: collapse to a single day.
    if (c === 'biweekly') setDaysOfWeek((prev) => [prev[0] ?? 'MON'])
  }

  const dayNames = DOW.filter((d) => daysOfWeek.includes(d.key)).map((d) => d.label).join(', ')
  const cadenceSummary =
    cadence === 'daily' ? 'every day'
    : cadence === 'weekly' ? `every week on ${dayNames || 'Mon'}`
    : cadence === 'biweekly' ? `every 2 weeks on ${dayNames || 'Mon'}`
    : `every month on day ${dayOfMonth}`
  const lookback = cadence === 'daily' ? 1 : cadence === 'weekly' ? 7 : cadence === 'biweekly' ? 14 : 30

  const save = useMutation({
    mutationFn: () => upsertFacilitySchedule({
      cadence,
      days_of_week: (cadence === 'weekly' || cadence === 'biweekly') ? daysOfWeek : [],
      day_of_month: cadence === 'monthly' ? dayOfMonth : null,
      send_hour_local: sendHour,
      send_minute_local: sendMin,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      lookback_days: lookback,
      active: enabled,
      recipients: [],
    }, activeFacilityId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['schedule'] }); toast.success('Saved', 'Schedule updated.'); onClose() },
    onError: (err) => { toast.error('Save failed', err instanceof ApiError ? err.message : 'Try again.') },
  })

  function handleTimeChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const [h, m] = e.target.value.split(':').map(Number)
    if (h !== undefined && !isNaN(h)) setSendHour(h)
    if (m !== undefined && !isNaN(m)) setSendMin(m)
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="lg"
      title="Schedule settings"
      subtitle="Vitals send automatically on your chosen interval"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">Cancel</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} leftIcon={<Check size={14} strokeWidth={2.5} />}>
            Save settings
          </Button>
        </>
      }
    >

      {/* ── Status card ─────────────────────────────────────────────────── */}
      <div className="ss-status-card">
        <div className="ss-status-left">
          <div className="ss-status-icon"><Clock size={18} /></div>
          <div>
            <p className="ss-status-title">Automatic sending</p>
            <p className="ss-status-body">
              {enabled
                ? <>Sends <strong>{cadenceSummary}</strong> at <strong>{formatDisplayTime(sendHour, sendMin)}</strong> via <strong>Fax</strong> to each resident&apos;s PCP — <strong>{readyCount}</strong> of {totalCount} ready.</>
                : 'Disabled — save to apply'}
            </p>
          </div>
        </div>
        <Toggle checked={enabled} onChange={setEnabled} />
      </div>

      {/* ── Send vitals via ─────────────────────────────────────────────── */}
      <div className="ss-section">
        <p className="ss-label">SEND VITALS VIA</p>
        <div className="ss-fax-chip">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9V4h12v5M6 18h12v3H6zM4 9h16a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z" />
          </svg>
          Fax <span className="ss-fax-chip-note">· only delivery method</span>
        </div>
      </div>

      {/* ── Send interval ───────────────────────────────────────────────── */}
      <div className="ss-section">
        <p className="ss-label">SEND INTERVAL</p>
        <div className="ss-interval-row">
          <select
            className="select ss-select"
            value={cadence}
            onChange={e => changeCadence(e.target.value as typeof cadence)}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="biweekly">Biweekly</option>
            <option value="monthly">Monthly</option>
          </select>
          {cadence === 'monthly' && (
            <>
              <span className="ss-at-label">on day</span>
              <select
                className="select ss-select ss-dom-select"
                value={dayOfMonth}
                onChange={e => setDayOfMonth(Number(e.target.value))}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </>
          )}
          <span className="ss-at-label">at</span>
          <input
            type="time"
            className="ss-time-input"
            value={hhmm(sendHour, sendMin)}
            onChange={handleTimeChange}
          />
        </div>

        {(cadence === 'weekly' || cadence === 'biweekly') && (
          <>
            <div className="ss-dow-row">
              {DOW.map(d => (
                <button
                  key={d.key}
                  type="button"
                  className={'ss-dow' + (daysOfWeek.includes(d.key) ? ' ss-dow--on' : '')}
                  onClick={() => toggleDay(d.key)}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <p className="ss-hint">
              {cadence === 'weekly' ? 'Pick one or more days.' : 'Pick one day — sends every 2 weeks on that day.'}
            </p>
          </>
        )}
        {cadence === 'monthly' && dayOfMonth > 28 && (
          <p className="ss-hint">Months with fewer days will send on their last day.</p>
        )}
      </div>

      {/* ── Recipients — each resident's PCP ────────────────────────────── */}
      <div className="ss-section ss-section--last">
        <p className="ss-label">RECIPIENTS — EACH RESIDENT&apos;S PCP</p>
        <p className="ss-recipients-desc">Each resident&apos;s vitals fax to their own primary care provider.</p>

        <button
          type="button"
          className="ss-patients-card"
          onClick={() => { onOpenPatients?.() }}
        >
          <div className="ss-patients-card-left">
            <svg className="ss-patients-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <div>
              <p className="ss-patients-count"><strong>{totalCount} patient{totalCount !== 1 ? 's' : ''}</strong></p>
              <p className="ss-patients-hint">Tap to manage residents, PCPs &amp; fax numbers</p>
            </div>
          </div>
          <span className="ss-patients-manage">Manage <span aria-hidden="true">›</span></span>
        </button>

        {totalCount === 0 && (
          <p className="ss-no-residents">No residents yet — add one or import a CSV.</p>
        )}
      </div>

    </Drawer>
  )
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={'toggle' + (checked ? ' toggle-on' : '')}
      onClick={() => onChange(!checked)}
    >
      <span className="toggle-thumb" aria-hidden="true" />
    </button>
  )
}
